/**
 * @contract
 *   Назначение:  Диалог добавления отзыва о ЧОПе (client-компонент)
 *   UX:          Кнопка → Dialog → форма (имя, роль, рейтинг звёздами, текст, honeypot)
 *   Антиспам:    Невидимое honeypot-поле
 *   Статус:      После submit — «Отзыв отправлен на модерацию»
 *   Автор:       AI Agent (Фаза 46, Спринт 2)
 *   Дата:        2026-03-10
 */

"use client";

import React, { useState } from "react";
import { Star, MessageCirclePlus, Send, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { Input } from "@oxpa/shared/ui/input";
import { Label } from "@oxpa/shared/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@oxpa/shared/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@oxpa/shared/ui/select";
import { useReviewSubmit } from "@/features/seo-portal/hooks/use-review-submit";

interface AddReviewDialogProps {
  companyId: string;
  companyName: string;
  variant?: "outline" | "primary";
}

export function AddReviewDialog({
  companyId,
  companyName,
  variant = "outline",
}: AddReviewDialogProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"form" | "done">("form");

  const { state, errorMessage, submit, reset: resetHook } = useReviewSubmit({
    customValidate: (payload) => {
      if (!payload.author_name.trim()) {
        return "Пожалуйста, укажите ваше имя.";
      }
      return null;
    },
  });

  const loading = state === "submitting";

  // Поля формы
  const [authorName, setAuthorName] = useState("");
  const [authorRole, setAuthorRole] = useState("anonymous");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [content, setContent] = useState("");
  const [honeypot, setHoneypot] = useState(""); // должно оставаться пустым

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0 || content.length < 20) return;
    // Валидация имени — через customValidate в хуке (показывает ошибку, а не молча выходит)

    const success = await submit({
      chop_id: companyId,
      author_name: authorName.trim(),
      author_role: authorRole,
      rating,
      content: content.trim(),
      honeypot,
    });

    if (success) {
      setStep("done");
    }
    // ошибка отображается через errorMessage из хука
  };

  // Сброс формы с задержкой под анимацию закрытия
  const resetFormAfterClose = () => {
    setTimeout(() => {
      setStep("form");
      resetHook();
      setAuthorName("");
      setAuthorRole("anonymous");
      setRating(0);
      setHoverRating(0);
      setContent("");
      setHoneypot("");
    }, 300);
  };

  const handleClose = () => {
    setOpen(false);
    resetFormAfterClose();
  };

  return (
    <>
      {variant === "primary" ? (
        <Button
          onClick={() => setOpen(true)}
          className="gap-2 bg-white text-black hover:bg-white/90 rounded-sm font-black uppercase tracking-wider text-xs"
        >
          <MessageCirclePlus className="w-4 h-4" />
          Написать отзыв
        </Button>
      ) : (
        <Button
          variant="outline"
          onClick={() => setOpen(true)}
          className="gap-2 border-white/20 hover:bg-white/10 text-white rounded-sm font-bold uppercase tracking-wider text-xs"
        >
          <MessageCirclePlus className="w-4 h-4" />
          Оставить отзыв
        </Button>
      )}

      <Dialog open={open} onOpenChange={(v) => {
        setOpen(v);
        if (!v) resetFormAfterClose();
      }}>
        <DialogContent className="bg-black border-white/20 text-white rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-black uppercase tracking-tight text-white">
              {step === "done" ? "Отзыв отправлен" : `Отзыв о ${companyName}`}
            </DialogTitle>
          </DialogHeader>

          {step === "done" ? (
            <div className="py-6 flex flex-col items-center justify-center gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-green-500" />
              </div>
              <div>
                <p className="font-bold text-white mb-1">
                  Спасибо за ваш отзыв!
                </p>
                <p className="text-white/50 text-sm font-sans max-w-xs mx-auto">
                  Отзыв отправлен на модерацию и появится на сайте после
                  проверки (обычно в течение 24 часов).
                </p>
              </div>
              <Button
                onClick={handleClose}
                variant="outline"
                className="border-white/20 hover:bg-white/10 text-white rounded-sm"
              >
                Закрыть
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Honeypot — скрыт от пользователя, видим для ботов */}
              <input
                type="text"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                aria-hidden="true"
                className="absolute opacity-0 pointer-events-none w-0 h-0"
              />

              {/* Имя */}
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs uppercase tracking-widest">
                  Ваше имя *
                </Label>
                <Input
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  placeholder='Например, "Анонимный охранник" или ФИО'
                  required
                  maxLength={100}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30 rounded-sm"
                />
              </div>

              {/* Роль */}
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs uppercase tracking-widest">
                  Кем вы являетесь?
                </Label>
                <Select value={authorRole} onValueChange={setAuthorRole}>
                  <SelectTrigger className="bg-white/5 border-white/20 text-white rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/20 text-white">
                    <SelectItem value="employee">Сотрудник / Охранник</SelectItem>
                    <SelectItem value="client">Клиент</SelectItem>
                    <SelectItem value="partner">Партнёр</SelectItem>
                    <SelectItem value="anonymous">Аноним</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Рейтинг звёздами */}
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs uppercase tracking-widest">
                  Оценка *
                </Label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-1 rounded transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-7 h-7 transition-colors ${
                          star <= (hoverRating || rating)
                            ? "fill-orange-400 text-amber-600"
                            : "fill-transparent text-white/20"
                        }`}
                      />
                    </button>
                  ))}
                  {rating > 0 && (
                    <span className="ml-2 text-sm text-white/50">
                      {["", "Плохо", "Так себе", "Нормально", "Хорошо", "Отлично"][rating]}
                    </span>
                  )}
                </div>
              </div>

              {/* Текст отзыва */}
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs uppercase tracking-widest">
                  Текст отзыва *
                  <span className="ml-2 text-white/30 normal-case">
                    (мин. 20 символов)
                  </span>
                </Label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Поделитесь опытом: условия работы, профессионализм руководства, отношение к сотрудникам..."
                  required
                  minLength={20}
                  maxLength={2000}
                  rows={4}
                  className="w-full bg-white/5 border border-white/20 text-white placeholder:text-white/30 rounded-sm p-3 text-sm font-sans resize-none focus:outline-none focus:ring-1 focus:ring-white/40"
                />
                <p className="text-[11px] text-white/30 text-right">
                  {content.length}/2000
                </p>
              </div>

              {/* Дисклеймер */}
              <p className="text-[10px] text-white/30 font-sans text-center uppercase tracking-widest">
                Отзыв пройдёт модерацию перед публикацией. Не публикуем
                оскорбления и личные данные.
              </p>

              {/* Ошибка */}
              {state === "error" && errorMessage && (
                <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
                  <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm font-sans">{errorMessage}</p>
                </div>
              )}

              {/* Кнопка */}
              <Button
                type="submit"
                disabled={loading || rating === 0 || content.length < 20}
                className="w-full bg-white text-black hover:bg-white/90 rounded-sm font-black uppercase tracking-wider text-xs gap-2"
              >
                <Send className="w-4 h-4" />
                {loading ? "Отправляем..." : "Отправить отзыв"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
